import React, { useRef } from 'react';
import { Plus, Trash2, X, ArrowLeft, ArrowRight, Upload, Play, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/services/api';

// Limits mirror the Chunk 1 backend contract (docs/DJ_GALLERY_FEATURE_STATUS.md).
export const GAL_IMG_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const GAL_VID_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
export const GAL_IMG_MAX_BYTES = 5 * 1024 * 1024;
export const GAL_VID_MAX_BYTES = 25 * 1024 * 1024;
export const GAL_MAX_POSTS = 20;
export const GAL_MAX_IMAGES = 5;
export const GAL_MAX_VIDEOS = 2;
export const GAL_MAX_MEDIA = 7;
export const GAL_VIDEO_MAX_SECONDS = 30;

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

export function emptyGalleryPost() {
  return { key: uid(), event_name: '', caption: '', media: [] };
}

// Seed a composer media item from an already-stored gallery media object
// (owner view: { type, url, duration, ... }). The served url doubles as preview.
export function mediaItemFromExisting(m) {
  return {
    localId: uid(),
    type: m.type,
    url: m.url,
    previewUrl: m.url,
    duration: m.duration ?? null,
    uploading: false,
    error: null,
  };
}

// Seed a composer post (single-post owner editor) from an existing gallery post.
export function postFromExisting(post) {
  return {
    key: uid(),
    event_name: post?.event_name || '',
    caption: post?.caption || '',
    media: (post?.media || []).map(mediaItemFromExisting),
  };
}

// Read a video's duration via browser metadata; resolves null if unavailable.
function readVideoDuration(file) {
  return new Promise((resolve) => {
    try {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.onloadedmetadata = () => {
        const d = v.duration;
        try { URL.revokeObjectURL(v.src); } catch (_) {}
        resolve(Number.isFinite(d) ? d : null);
      };
      v.onerror = () => resolve(null);
      v.src = URL.createObjectURL(file);
    } catch (_) { resolve(null); }
  });
}

// Submit-time validation. Returns an error string (friendly) or null when OK.
export function validateGalleryPosts(posts) {
  if (!posts || posts.length === 0) {
    return 'Please add at least one gallery post with an image or video.';
  }
  if (posts.length > GAL_MAX_POSTS) {
    return `You can add at most ${GAL_MAX_POSTS} gallery posts.`;
  }
  for (const p of posts) {
    const ready = (p.media || []).filter((m) => m.url);
    const anyUploading = (p.media || []).some((m) => m.uploading);
    if (ready.length === 0) {
      return 'Please add at least one image or video to each gallery post.';
    }
    const hasText = (p.event_name && p.event_name.trim()) || (p.caption && p.caption.trim());
    if (!hasText) {
      return 'Please add a caption or event name for each gallery post.';
    }
    const imgs = ready.filter((m) => m.type === 'image').length;
    const vids = ready.filter((m) => m.type === 'video').length;
    if (imgs > GAL_MAX_IMAGES) return `Each gallery post can have at most ${GAL_MAX_IMAGES} images.`;
    if (vids > GAL_MAX_VIDEOS) return `Each gallery post can have at most ${GAL_MAX_VIDEOS} videos.`;
    if (ready.length > GAL_MAX_MEDIA) return `Each gallery post can have at most ${GAL_MAX_MEDIA} media items.`;
    if (anyUploading) return 'Please wait for all media to finish uploading.';
  }
  return null;
}

// Build the backend `gallery_posts` payload. Stores only safe URL references,
// never raw file paths. Position is the array order (carousel order).
export function galleryPostsToPayload(posts) {
  return (posts || []).map((p) => ({
    event_name: p.event_name && p.event_name.trim() ? p.event_name.trim() : null,
    caption: p.caption && p.caption.trim() ? p.caption.trim() : null,
    media: (p.media || [])
      .filter((m) => m.url)
      .map((m, i) => ({ url: m.url, position: i, duration: m.duration ?? null })),
  }));
}

/**
 * Controlled gallery composer. Props:
 *  - posts:    array of { key, event_name, caption, media: [{ localId, type, url, previewUrl, duration, uploading, error }] }
 *  - setPosts: React state setter (functional updates used throughout)
 *  - disabled: bool (e.g. while the form is submitting)
 *  - hideAddPost / hideRemovePost / headerless: single-post (owner editor) reuse
 */
export default function GalleryComposer({ posts, setPosts, disabled = false,
  hideAddPost = false, hideRemovePost = false, headerless = false }) {
  const fileRefs = useRef({});

  const patchPost = (postKey, patch) =>
    setPosts((prev) => prev.map((p) => (p.key === postKey ? { ...p, ...patch } : p)));

  const patchMedia = (postKey, localId, patch) =>
    setPosts((prev) =>
      prev.map((p) =>
        p.key === postKey
          ? { ...p, media: p.media.map((m) => (m.localId === localId ? { ...m, ...patch } : m)) }
          : p
      )
    );

  const addPost = () => {
    setPosts((prev) => {
      if (prev.length >= GAL_MAX_POSTS) {
        toast.error(`You can add at most ${GAL_MAX_POSTS} gallery posts.`);
        return prev;
      }
      return [...prev, emptyGalleryPost()];
    });
  };

  const removePost = (postKey) =>
    setPosts((prev) => {
      const p = prev.find((x) => x.key === postKey);
      (p?.media || []).forEach((m) => { try { URL.revokeObjectURL(m.previewUrl); } catch (_) {} });
      return prev.filter((x) => x.key !== postKey);
    });

  const removeMedia = (postKey, localId) =>
    setPosts((prev) =>
      prev.map((p) => {
        if (p.key !== postKey) return p;
        const m = p.media.find((x) => x.localId === localId);
        if (m) { try { URL.revokeObjectURL(m.previewUrl); } catch (_) {} }
        return { ...p, media: p.media.filter((x) => x.localId !== localId) };
      })
    );

  const moveMedia = (postKey, localId, dir) =>
    setPosts((prev) =>
      prev.map((p) => {
        if (p.key !== postKey) return p;
        const arr = [...p.media];
        const i = arr.findIndex((x) => x.localId === localId);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= arr.length) return p;
        [arr[i], arr[j]] = [arr[j], arr[i]];
        return { ...p, media: arr };
      })
    );

  const onPickMedia = async (post, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    // Snapshot counts so a multi-file selection respects per-post limits.
    let imgs = post.media.filter((m) => m.type === 'image').length;
    let vids = post.media.filter((m) => m.type === 'video').length;
    let total = post.media.length;

    for (const file of files) {
      const isImg = GAL_IMG_TYPES.includes(file.type);
      const isVid = GAL_VID_TYPES.includes(file.type);
      if (!isImg && !isVid) { toast.error('Use JPG/PNG/WebP images or MP4/MOV/WebM videos.'); continue; }
      if (isImg && file.size > GAL_IMG_MAX_BYTES) { toast.error('Image too large (max 5 MB).'); continue; }
      if (isVid && file.size > GAL_VID_MAX_BYTES) { toast.error('Video too large (max 25 MB).'); continue; }
      if (total + 1 > GAL_MAX_MEDIA) { toast.error(`Max ${GAL_MAX_MEDIA} media items per post.`); break; }
      if (isImg && imgs + 1 > GAL_MAX_IMAGES) { toast.error(`Max ${GAL_MAX_IMAGES} images per post.`); continue; }
      if (isVid && vids + 1 > GAL_MAX_VIDEOS) { toast.error(`Max ${GAL_MAX_VIDEOS} videos per post.`); continue; }

      let duration = null;
      if (isVid) {
        duration = await readVideoDuration(file);
        if (duration != null && duration > GAL_VIDEO_MAX_SECONDS) {
          toast.error(`Each video must be ${GAL_VIDEO_MAX_SECONDS} seconds or shorter.`);
          continue;
        }
        if (duration == null) {
          toast.message('Could not read video length in the browser — the server will check the 30s limit on submit.');
        }
      }

      const localId = uid();
      const previewUrl = URL.createObjectURL(file);
      const type = isImg ? 'image' : 'video';
      imgs += isImg ? 1 : 0; vids += isVid ? 1 : 0; total += 1;
      // optimistic add (uploading)
      setPosts((prev) =>
        prev.map((p) =>
          p.key === post.key
            ? { ...p, media: [...p.media, { localId, type, url: null, previewUrl, duration, uploading: true, error: null }] }
            : p
        )
      );
      try {
        const res = await api.uploadGalleryMedia(file);   // returns { url, type, duration, ... }
        patchMedia(post.key, localId, { url: res.url, uploading: false, duration: duration ?? res.duration ?? null });
      } catch (err) {
        patchMedia(post.key, localId, { uploading: false, error: err.message || 'Upload failed' });
        toast.error(err.message || 'Upload failed');
      }
    }
  };

  return (
    <div className="space-y-3">
      {!headerless && (
        <div>
          <Label className="text-base">Gallery Posts *</Label>
          <p className="text-xs opacity-60 mt-1">Add at least one post that shows your previous DJ work.</p>
        </div>
      )}

      {posts.map((post, idx) => {
        const imgs = post.media.filter((m) => m.type === 'image').length;
        const vids = post.media.filter((m) => m.type === 'video').length;
        return (
          <div key={post.key} className="rounded-md border border-border/60 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium opacity-80">{hideRemovePost ? 'Post details' : `Post ${idx + 1}`}</span>
              {!hideRemovePost && (
                <Button type="button" variant="ghost" size="sm" onClick={() => removePost(post.key)} disabled={disabled}>
                  <Trash2 className="h-4 w-4 mr-1" /> Remove post
                </Button>
              )}
            </div>

            <div>
              <Label htmlFor={`ev-${post.key}`}>Event name (optional)</Label>
              <Input id={`ev-${post.key}`} value={post.event_name}
                onChange={(e) => patchPost(post.key, { event_name: e.target.value })}
                placeholder="e.g. Summer Rooftop Set" disabled={disabled} />
            </div>
            <div>
              <Label htmlFor={`cap-${post.key}`}>Caption (optional)</Label>
              <Textarea id={`cap-${post.key}`} rows={2} value={post.caption}
                onChange={(e) => patchPost(post.key, { caption: e.target.value })}
                placeholder="Say something about this set…" disabled={disabled} />
            </div>

            {post.media.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {post.media.map((m, mi) => (
                  <div key={m.localId} className="relative h-20 w-20 rounded-md overflow-hidden border border-border/60 bg-muted/40">
                    {m.type === 'image' ? (
                      <img src={m.previewUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <>
                        <video src={m.previewUrl} className="h-full w-full object-cover" muted />
                        <span className="absolute inset-0 flex items-center justify-center">
                          <Play className="h-6 w-6 text-white drop-shadow" />
                        </span>
                      </>
                    )}
                    {m.uploading && (
                      <span className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <Loader2 className="h-5 w-5 animate-spin text-white" />
                      </span>
                    )}
                    {m.error && <span className="absolute inset-0 bg-red-600/60" title={m.error} />}
                    <button type="button" onClick={() => removeMedia(post.key, m.localId)} disabled={disabled}
                      className="absolute top-0.5 right-0.5 rounded bg-black/60 p-0.5 text-white" aria-label="Remove media">
                      <X className="h-3 w-3" />
                    </button>
                    <div className="absolute bottom-0.5 left-0.5 flex gap-0.5">
                      <button type="button" onClick={() => moveMedia(post.key, m.localId, -1)} disabled={disabled || mi === 0}
                        className="rounded bg-black/60 p-0.5 text-white disabled:opacity-30" aria-label="Move left">
                        <ArrowLeft className="h-3 w-3" />
                      </button>
                      <button type="button" onClick={() => moveMedia(post.key, m.localId, 1)} disabled={disabled || mi === post.media.length - 1}
                        className="rounded bg-black/60 p-0.5 text-white disabled:opacity-30" aria-label="Move right">
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-3">
              <input
                ref={(el) => (fileRefs.current[post.key] = el)}
                type="file" multiple
                accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
                className="hidden"
                onChange={(e) => { onPickMedia(post, e.target.files); e.target.value = ''; }}
              />
              <Button type="button" variant="outline" size="sm" disabled={disabled}
                onClick={() => fileRefs.current[post.key]?.click()}>
                <Upload className="h-4 w-4 mr-2" /> Add media
              </Button>
              <span className="text-xs opacity-60">{imgs}/{GAL_MAX_IMAGES} images · {vids}/{GAL_MAX_VIDEOS} videos</span>
            </div>
            <p className="text-xs opacity-50">
              Images JPG/PNG/WebP ≤5 MB · Videos MP4/MOV/WebM ≤25 MB, ≤30s · up to {GAL_MAX_MEDIA} per post.
              Multiple media show as a carousel.
            </p>
          </div>
        );
      })}

      {!hideAddPost && (
        <Button type="button" variant="outline" onClick={addPost} disabled={disabled || posts.length >= GAL_MAX_POSTS}>
          <Plus className="h-4 w-4 mr-2" /> Add Gallery Post
        </Button>
      )}
    </div>
  );
}
